import React from 'react';
import PropTypes from 'prop-types';
import Table from '../../../components/Table';
import { Box } from '@mui/material';
import NoData from '../../../components/NoData';
import {
  MONTHLY_OPPS_SEASONALITY,
  MONTHLY_SPENDS_SEASONALITY,
  OPPS_DATA,
  WEEKLY_OPPS_SEASONALITY,
  WEEKLY_SPENDS_SEASONALITY,
} from '../constants';
import { ShimmerComponent } from '../../../components/Shimmer/ShimmerComponent';
import BouncingLogo from '../../../components/Shimmer/BouncingLogo';

const WeeklySeasonalityHeader = ({ type }) => (
  <span style={{ width: '200px', display: 'inline-block' }}>
    {type === OPPS_DATA ? 'Weekly Opps Seasonality' : 'Weekly Spends Seasonality'}
  </span>
);

WeeklySeasonalityHeader.propTypes = {
  type: PropTypes.any.isRequired,
};

// Helper function to create weekly header object
// Uses a closure over `type` so the Header component always renders the correct label
// regardless of how react-table passes props to the Header renderer.
const createWeeklyOppsHeader = (type) => ({
  Header: () => (
    <span style={{ width: '200px', display: 'inline-block' }}>
      {type === OPPS_DATA ? 'Weekly Opps Seasonality' : 'Weekly Spends Seasonality'}
    </span>
  ),
  accessor: type === OPPS_DATA ? WEEKLY_OPPS_SEASONALITY : WEEKLY_SPENDS_SEASONALITY,
});

const OppsSeasonalityTableView = ({ viewType, data, headers, isLoading, error, type }) => {
  const weeklyOppsHeader = createWeeklyOppsHeader(type);

  const getOppsHeaders = (headers) => [weeklyOppsHeader, ...headers];

  const renderContent = () => {
    if (error) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '50px' }}>
          <NoData textValue="Error loading data" />
        </Box>
      );
    } else if (isLoading) {
      return (
        <ShimmerComponent
          updatedStyle={{
            width: '90%',
            height: '70%',
            backgroundSize: '2000px 800px',
            border: '0px',
            margin: 'auto',
            padding: '0 20px',
          }}
        >
          <BouncingLogo height="48vh" text="Loading..." />
        </ShimmerComponent>
      );
    } else if (!isLoading && (!data || data.length === 0)) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '50px' }}>
          <NoData />
        </Box>
      );
    } else {
      return (
        <div style={{ width: '90%', margin: 'auto' }}>
          <Table
            boldHeaders={true}
            boldDataColumns={[
              WEEKLY_OPPS_SEASONALITY,
              WEEKLY_SPENDS_SEASONALITY,
              MONTHLY_OPPS_SEASONALITY,
              MONTHLY_SPENDS_SEASONALITY,
            ]}
            stickyColumns={[
              WEEKLY_OPPS_SEASONALITY,
              WEEKLY_SPENDS_SEASONALITY,
              MONTHLY_OPPS_SEASONALITY,
              MONTHLY_SPENDS_SEASONALITY,
            ]}
            data={data}
            columns={viewType === 'Weekly' ? getOppsHeaders(headers) : headers}
          />
        </div>
      );
    }
  };

  return (
    <>
      <h3 style={{ textAlign: 'left', width: '90%', margin: 'auto', paddingTop: '40px' }}>
        {`${viewType} ${type === OPPS_DATA ? 'Opps' : 'Spends'} Seasonality`}
      </h3>
      {renderContent()}
    </>
  );
};

// Prop validation
OppsSeasonalityTableView.propTypes = {
  viewType: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  headers: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  type: PropTypes.any,
};

export default OppsSeasonalityTableView;
